/**
 * features/timetable/NakoLaunch.tsx — Academic Admin → Timetable editor.
 * ------------------------------------------------------------------
 * The door into Nako, the timetable editor (2 Nov 2026). The sidebar link
 * opens Nako signed in straight away (launchNako, inside the click, because a
 * browser blocks a tab opened later) and lands on this screen, which says how
 * it went and offers a button to try again — a blocked tab, or a deep link to
 * #/nako, needs a click of its own.
 * ------------------------------------------------------------------
 */
import { useSyncExternalStore } from 'react';
import { CalendarCog, ExternalLink, Loader2 } from 'lucide-react';
import { AccentButton, PageHeader, SoftCard } from '@/features/finance/ui';
import { NAKO_URL, openNako } from '@/lib/nako';

type NakoStatus = { state: 'idle' | 'opening' | 'done' } | { state: 'failed'; message: string };

let status: NakoStatus = { state: 'idle' };
const listeners = new Set<() => void>();
const set = (s: NakoStatus) => { status = s; listeners.forEach((l) => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** Opens Nako signed in. Call from a click handler. */
export function launchNako() {
  set({ state: 'opening' });
  openNako().then(
    () => set({ state: 'done' }),
    (e: Error) => set({ state: 'failed', message: e.message }),
  );
}

export default function NakoLaunch() {
  const shown = useSyncExternalStore(subscribe, () => status);

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Timetable editor" icon={CalendarCog}
        subtitle="Nako: build, fix and solve the timetable. It opens in its own tab, signed in as you." />
      <SoftCard>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {shown.state === 'opening' && <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Opening Nako and signing you in…</span>}
            {shown.state === 'done' && 'Nako is open in another tab, signed in as you. The latest draft opens by itself.'}
            {shown.state === 'failed' && <span className="text-rose-600 dark:text-rose-400">{shown.message}</span>}
            {shown.state === 'idle' && 'Open the editor in a new tab. You will not need to sign in again.'}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <AccentButton icon={ExternalLink} disabled={shown.state === 'opening'} onClick={launchNako}>
              {shown.state === 'idle' ? 'Open Nako' : 'Open Nako again'}
            </AccentButton>
            <a href={NAKO_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-primaryLight hover:underline">
              or open it and sign in with your password
            </a>
          </div>
        </div>
      </SoftCard>
    </div>
  );
}
